import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import api from "@/lib/api";

const initial = { name: "", company: "", phone: "", email: "", message: "" };

export default function ContactForm({ variant = "light" }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const isDark = variant === "dark";

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!form.name || !form.email || !form.message) {
      toast.error("Preencha nome, e-mail e mensagem.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      if (data?.email_sent) {
        toast.success("Mensagem enviada! Retornaremos em breve.");
      } else {
        toast.success("Recebemos seu contato. Nossa equipe retornará em breve.");
      }
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Não foi possível enviar. Tente novamente em instantes.";
      toast.error(typeof msg === "string" ? msg : "Erro ao enviar.");
    } finally {
      setLoading(false);
    }
  };

  const inputBase = isDark
    ? "w-full bg-transparent border-b border-gray-700 focus:border-white text-white placeholder:text-gray-500 py-3 outline-none transition-colors"
    : "w-full bg-transparent border-b border-gray-300 focus:border-[#1E3A8A] text-[#111827] placeholder:text-gray-400 py-3 outline-none transition-colors";
  const labelBase = isDark
    ? "block font-mono-axis text-xs uppercase tracking-widest text-gray-400 mb-2"
    : "block font-mono-axis text-xs uppercase tracking-widest text-gray-500 mb-2";

  return (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8"
      noValidate
    >
      <div className="md:col-span-1">
        <label htmlFor="name" className={labelBase}>
          Nome *
        </label>
        <input
          id="name"
          type="text"
          data-testid="contact-input-name"
          value={form.name}
          onChange={setField("name")}
          className={inputBase}
          placeholder="Seu nome completo"
          autoComplete="name"
          required
        />
      </div>

      <div className="md:col-span-1">
        <label htmlFor="company" className={labelBase}>
          Empresa
        </label>
        <input
          id="company"
          type="text"
          data-testid="contact-input-company"
          value={form.company}
          onChange={setField("company")}
          className={inputBase}
          placeholder="Hospital, clínica ou instituição"
          autoComplete="organization"
        />
      </div>

      <div className="md:col-span-1">
        <label htmlFor="phone" className={labelBase}>
          Telefone
        </label>
        <input
          id="phone"
          type="tel"
          data-testid="contact-input-phone"
          value={form.phone}
          onChange={setField("phone")}
          className={inputBase}
          placeholder="+55 (00) 00000-0000"
          autoComplete="tel"
        />
      </div>

      <div className="md:col-span-1">
        <label htmlFor="email" className={labelBase}>
          E-mail *
        </label>
        <input
          id="email"
          type="email"
          data-testid="contact-input-email"
          value={form.email}
          onChange={setField("email")}
          className={inputBase}
          placeholder="seu.email@empresa.com"
          autoComplete="email"
          required
        />
      </div>

      <div className="md:col-span-2">
        <label htmlFor="message" className={labelBase}>
          Mensagem *
        </label>
        <textarea
          id="message"
          rows={5}
          data-testid="contact-input-message"
          value={form.message}
          onChange={setField("message")}
          className={`${inputBase} resize-none`}
          placeholder="Como podemos ajudar sua instituição?"
          required
        />
      </div>

      <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">
        <p
          className={`text-xs ${
            isDark ? "text-gray-500" : "text-gray-500"
          } max-w-md`}
        >
          Ao enviar, você concorda com nossa política de privacidade e o
          tratamento de dados conforme a LGPD.
        </p>
        <button
          type="submit"
          data-testid="contact-submit"
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              Enviar mensagem
              <Send className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
